import { useEffect, useState } from 'react'
import { getProfile, postLogin, postLogout } from '../libs/login-service'
import { useNavigate } from 'react-router-dom'
import { styled } from 'styled-components'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'

const Wrapper = styled.div`
    width: 100%;
    height: 100vh;
    display: flex;
`
const Img = styled.img`
    width: 50%;
    height: 100%;
    object-fit: cover;
`
const ContentWrapper = styled.div`
    width: 50%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`

const Logo = styled.img`
    width: 120px;
`

const Title = styled.h1`
    color: #115ff5;
    margin: 10px 0;
`

const Subtitle = styled.p`
    color: #a4a2a1;
    font-size: 14px;
    margin: 5px 0 30px 0;
`

const Form = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 400px;
`

const Input = styled.input<{ error?: boolean }>`
    height: 40px;
    width: 100%;
    padding: 10px 15px;
    border: 2px solid ${(props) => (props.error ? '#d32f2f' : '#e5e6eb')};
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.2s;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${(props) => (props.error ? '#d32f2f' : '#115ff5')};
        box-shadow: ${(props) =>
            props.error
                ? '0 0 0 3px rgba(211, 47, 47, 0.1)'
                : '0 0 0 3px rgba(17, 95, 245, 0.1)'};
    }

    &:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
    }
`

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
`

const InputLabel = styled.label`
    color: #333;
    font-size: 14px;
    font-weight: 600;
`

const ErrorMessage = styled.span`
    color: #d32f2f;
    font-size: 13px;
    margin-top: 2px;
`

const SuccessMessage = styled.div`
    background-color: #e8f5f0;
    border-left: 4px solid #31c690;
    color: #188c5f;
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 20px;
    font-size: 14px;
`

const ErrorAlert = styled.div`
    background-color: #ffebee;
    border-left: 4px solid #d32f2f;
    color: #b71c1c;
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 20px;
    font-size: 14px;
`

const Button = styled.button<{ loading?: boolean; disabled?: boolean }>`
    padding: 12px 16px;
    margin: 20px 0;
    background-color: ${(props) => (props.disabled ? '#bdbdbd' : '#115ff5')};
    border: none;
    border-radius: 4px;
    color: white;
    font-size: 16px;
    font-weight: 600;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
        background-color: #0d47a1;
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }
`

const RegisterWrapper = styled.div`
    display: flex;
    justify-content: center;
    gap: 4px;
    font-size: 14px;
    color: #666;

    a {
        color: #115ff5;
        text-decoration: none;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
`

const LoadingSpinner = styled(AiOutlineLoading3Quarters)`
    animation: spin 1s linear infinite;
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`

interface Props {
    setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>
}

interface ValidationErrors {
    email?: string
    password?: string
}

export default function Login({ setIsLoggedIn }: Props) {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [errors, setErrors] = useState<ValidationErrors>({})
    const [loginError, setLoginError] = useState<string | null>(null)
    const navigate = useNavigate()

    const validateForm = (): boolean => {
        const newErrors: ValidationErrors = {}

        if (!email.trim()) {
            newErrors.email = 'Email is required'
        } else if (!email.includes('@')) {
            newErrors.email = 'Please enter a valid email'
        }

        if (!password) {
            newErrors.password = 'Password is required'
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const isFormValid = email.trim() !== '' && password !== ''

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault()
        setLoginError(null)

        if (!validateForm()) {
            return
        }

        setIsLoading(true)

        try {
            const body = {
                email: email.trim(),
                password: password,
            }
            const { success, userId, token } = await postLogin(body)

            if (success) {
                setIsLoggedIn(true)
                localStorage.setItem('userId', JSON.stringify(userId))
                localStorage.setItem('token', token)
                navigate('/')
            } else {
                setLoginError('Invalid email or password. Please try again.')
            }
        } catch (error) {
            setLoginError(
                'An error occurred during login. Please try again later.'
            )
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Wrapper>
            <Img src="/images/login/login.png" alt="Login" />
            <ContentWrapper>
                <div style={{ width: '100%', maxWidth: '400px' }}>
                    <div style={{ marginBottom: '40px' }}>
                        <Logo src="/images/login/logo.png" alt="MNG Logo" />
                        <Title>Welcome to MNG</Title>
                        <Subtitle>
                            Automated movement skill assessment solution
                        </Subtitle>
                    </div>

                    {loginError && <ErrorAlert>{loginError}</ErrorAlert>}

                    <Form onSubmit={onSubmit} noValidate>
                        <InputWrapper>
                            <InputLabel htmlFor="email">Email</InputLabel>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value)
                                    if (errors.email) {
                                        setErrors({ ...errors, email: '' })
                                    }
                                }}
                                onBlur={() => {
                                    if (email && !email.includes('@')) {
                                        setErrors({
                                            ...errors,
                                            email: 'Please enter a valid email',
                                        })
                                    }
                                }}
                                placeholder="Enter your email"
                                disabled={isLoading}
                                error={!!errors.email}
                            />
                            {errors.email && (
                                <ErrorMessage>{errors.email}</ErrorMessage>
                            )}
                        </InputWrapper>

                        <InputWrapper>
                            <InputLabel htmlFor="password">Password</InputLabel>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value)
                                    if (errors.password) {
                                        setErrors({ ...errors, password: '' })
                                    }
                                }}
                                placeholder="Enter your password"
                                disabled={isLoading}
                                error={!!errors.password}
                            />
                            {errors.password && (
                                <ErrorMessage>{errors.password}</ErrorMessage>
                            )}
                        </InputWrapper>

                        <Button
                            type="submit"
                            disabled={!isFormValid || isLoading}
                            loading={isLoading}
                        >
                            {isLoading && <LoadingSpinner size={18} />}
                            {isLoading ? 'Logging in...' : 'Login'}
                        </Button>
                    </Form>

                    <RegisterWrapper>
                        Don't have an account yet?&nbsp;
                        <a href="/register">Sign Up</a>
                    </RegisterWrapper>
                </div>
            </ContentWrapper>
        </Wrapper>
    )
}
